import React from 'react';
import { makeStyles } from '@material-ui/core';

import { Checkbox, FormControlLabel, FormGroup, units } from '@ha/dsl';

export interface Option {
  value: string;
  title: string | React.ReactNode;
  testLocator?: string;
  childElement?: React.ReactNode;
}

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  options: Option[];
  layout?: 'horizontal' | 'vertical';
  checkboxSize?: 'small' | 'medium';
}

const useStyles = makeStyles(() => ({
  // to reset global styles set by app.bundle.css
  formControlLabel: {
    marginBottom: 0,
    '&:last-child': {
      marginRight: 0,
    },
  },
  checkboxRoot: {
    padding: units.rem(0.5),
  },
  label: {
    width: '100%',
  },
  root: {
    marginRight: 0,
  },
  formGroup: {
    width: '100%',
  },
}));

const CommonCheckboxFilter: React.FC<Props> = ({
  value,
  onChange,
  options,
  layout = 'vertical',
  checkboxSize = 'medium',
}) => {
  const classes = useStyles();

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const toggledOption = event.target.value;

      onChange(
        options
          .map(option => option.value)
          .filter(option => {
            if (option === toggledOption) {
              return checked;
            }

            return value.includes(option);
          }),
      );
    },
    [onChange, options, value],
  );

  return (
    <FormGroup row={layout === 'horizontal'} className={classes.formGroup}>
      {options.map(
        ({ value: optionValue, title, childElement, testLocator }) => (
          <React.Fragment key={optionValue}>
            <FormControlLabel
              className={classes.formControlLabel}
              classes={{
                root: layout === 'vertical' ? classes.root : undefined,
                label: classes.label,
              }}
              control={
                <Checkbox
                  color="primary"
                  size={checkboxSize}
                  checked={value.includes(optionValue)}
                  onChange={handleChange}
                  value={optionValue}
                  name={optionValue}
                  data-test-locator={testLocator}
                  classes={{
                    root:
                      checkboxSize === 'small'
                        ? classes.checkboxRoot
                        : undefined,
                  }}
                />
              }
              data-test-locator={`${testLocator} Label`}
              label={title}
            />
            {childElement}
          </React.Fragment>
        ),
      )}
    </FormGroup>
  );
};

export default CommonCheckboxFilter;
